import type { Key } from 'swr';

import { ServiceError } from '@ricoh/ria.client.core/errors';

import type { Fetcher, FetcherWithKey, FetchHandler } from './_fetcher.type';

/**
 * Create fetcher.
 *
 * @template TRequestData Request data.
 * @template TResponseData Response data.
 * @param {Key} key Request key.
 * @param {FetchHandler<TRequestData, TResponseData>} handle Handler.
 *
 * @returns {Fetcher<TRequestData, TResponseData>} Fetcher.
 */
export function createFetcher<TRequestData = void, TResponseData = void>(
  key: Key,
  handle: FetchHandler<TRequestData, TResponseData>,
): Fetcher<TRequestData, TResponseData> {
  const handler: FetchHandler<TRequestData, TResponseData> = async (...args) => {
    try {
      return await handle(...args);
    } catch (exception) {
      throw ServiceError.create({
        status: 500,
        message: `Fetch failed.`,
        cause: exception,
      });
    }
  };

  const result: Fetcher<TRequestData, TResponseData> = {
    key,
    handler,

    fetch: (...args) => {
      const [_key, options] = args;
      return handler(options?.arg as TRequestData);
    },

    fetcherWith: (requestData) => {
      const fetcherWithKey: FetcherWithKey<TRequestData> = {
        $is: 'fetcherWithKey',
        key,
        arg: requestData,
      };

      return createFetcher(fetcherWithKey, () => handler(requestData));
    },
  };

  return result;
}
